import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeoMeta } from '@/hooks/useSeoMeta';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

const schemaJson = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Advertise on roameurope.blog',
  description:
    'Reach a highly engaged audience of relationship-focused readers. Explore direct advertising and sponsorship options on roameurope.blog.',
  url: `${BASE_URL}/advertise`,
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
  },
});

const stats = [
  { icon: 'ri-user-line', value: '120K+', label: 'Monthly Readers' },
  { icon: 'ri-time-line', value: '4:30', label: 'Avg. Time on Page' },
  { icon: 'ri-article-line', value: '30+', label: 'Articles Published' },
  { icon: 'ri-heart-line', value: '78%', label: 'Return Visitor Rate' },
];

const audience = [
  { icon: 'ri-women-line', label: 'Gender', value: '68% female, 32% male' },
  { icon: 'ri-map-pin-2-line', label: 'Top Markets', value: 'USA, UK, Canada, Australia' },
  { icon: 'ri-calendar-line', label: 'Core Age Range', value: '25–44 years old' },
  { icon: 'ri-smartphone-line', label: 'Device Split', value: '61% mobile, 39% desktop' },
  { icon: 'ri-search-eye-line', label: 'Traffic Source', value: '72% organic search' },
  { icon: 'ri-fire-line', label: 'Top Topics', value: 'Dating, Communication, Breakups' },
];

const placements = [
  {
    icon: 'ri-image-2-line',
    title: 'Hero Banner',
    slug: 'hero-banner',
    description:
      'Premium above-the-fold placement at the top of the homepage. Maximum visibility for brand launches and awareness campaigns.',
    size: '728 × 90 / 320 × 50 (mobile)',
    impressions: '~35,000 / month',
    price: '$180 / month',
    highlight: true,
  },
  {
    icon: 'ri-layout-right-line',
    title: 'Sidebar Rectangle',
    slug: 'sidebar-rectangle',
    description:
      'Persistent sidebar placement visible on all article pages. Ideal for retargeting, coaching services, or dating app promotions.',
    size: '300 × 250',
    impressions: '~90,000 / month',
    price: '$120 / month',
    highlight: false,
  },
  {
    icon: 'ri-file-text-line',
    title: 'In-Article Ad',
    slug: 'in-article',
    description:
      'Native-style placement embedded mid-content in articles. Contextually relevant and seen by highly engaged readers.',
    size: '728 × 90 / 320 × 100 (mobile)',
    impressions: '~55,000 / month',
    price: '$140 / month',
    highlight: false,
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Sponsored Article',
    slug: 'sponsored-article',
    description:
      'A full editorial-style article written in our voice, featuring your product or service naturally. Stays live permanently.',
    size: 'Full article (~800–1200 words)',
    impressions: '~8,000 in first 30 days',
    price: '$350 per post',
    highlight: false,
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Newsletter Mention',
    slug: 'newsletter',
    description:
      'A dedicated mention in our weekly email to subscribers. Direct, personal, and highly trusted by our audience.',
    size: '1 paragraph + CTA link',
    impressions: '~6,500 subscribers',
    price: '$90 per send',
    highlight: false,
  },
  {
    icon: 'ri-vip-crown-line',
    title: 'Brand Bundle',
    slug: 'bundle',
    description:
      'Combine hero banner + sidebar + sponsored article for a month-long full-site presence. Best value for maximum reach.',
    size: 'All placements combined',
    impressions: '~180,000 / month',
    price: '$560 / month',
    highlight: true,
  },
];

const faqs = [
  {
    q: 'Who reads roameurope.blog?',
    a: 'Our readers are primarily adults aged 25–44 who are actively navigating modern relationships — dating, communication, healing after breakups, and building healthy partnerships. Over 68% are women.',
  },
  {
    q: 'What ad formats do you accept?',
    a: 'We accept static image banners (JPEG/PNG), animated GIFs (under 150KB), and HTML5 creatives. For sponsored content, we write the piece ourselves to ensure it matches our editorial voice.',
  },
  {
    q: 'How is performance measured?',
    a: 'We provide monthly reports covering impressions, estimated click-through, and article engagement metrics. We use privacy-first analytics aligned with our GDPR-compliant setup.',
  },
  {
    q: 'Can I run ads alongside Google AdSense?',
    a: 'Yes — direct placements appear in designated slots that run independently of AdSense. There is no conflict, and direct ads are never served in AdSense slots.',
  },
  {
    q: 'What types of brands are a good fit?',
    a: 'Dating apps, relationship coaching services, therapy platforms, books on psychology/relationships, wellness brands, personal development tools, and lifestyle products targeting women 25–44.',
  },
  {
    q: 'Is there a minimum commitment?',
    a: 'Banner ads are available on a monthly rolling basis — no long-term contract required. Sponsored articles are one-time placements. Discounts apply for 3-month+ commitments.',
  },
];

export default function AdvertisePage() {
  useSeoMeta({
    title: 'Advertise on roameurope.blog | Relationship & Dating Audience',
    description:
      'Reach 120,000+ monthly readers passionate about relationships, dating, and self-love. Explore direct ad placements, sponsored articles, and newsletter sponsorships on roameurope.blog.',
    url: '/advertise',
    type: 'website',
    tags: ['advertise', 'relationship blog advertising', 'dating audience sponsorship'],
  });

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7bu1uehe76khnho4qsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />

      <main className="pt-24 pb-20 min-h-screen bg-white dark:bg-stone-950">

        {/* ── Hero ── */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/772ec2fa7d3cdc0e50272c7d8e7e8c96.jpg')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 via-stone-900/55 to-stone-900/70" />
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-24 text-center">
            <span className="inline-block px-3 py-1 rounded-full bg-rose-500/20 text-rose-300 text-xs font-semibold tracking-widest uppercase mb-5">
              Partner With Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: "'Lora', serif" }}>
              Reach Readers Who Care<br className="hidden md:block" /> About Relationships
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-8">
              roameurope.blog connects 120,000+ monthly readers with honest, thoughtful content about love, dating, and human connection. Partner with us to reach a deeply engaged, intent-driven audience.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-semibold px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line" /> Get in Touch
            </a>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="bg-stone-50 dark:bg-stone-900 border-y border-stone-100 dark:border-stone-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-500">
                  <i className={`${s.icon} text-lg`} />
                </div>
                <p className="text-3xl font-bold text-stone-800 dark:text-stone-100" style={{ fontFamily: "'Lora', serif" }}>{s.value}</p>
                <p className="text-xs text-stone-400 dark:text-stone-500 mt-1 uppercase tracking-wide font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Audience ── */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-3" style={{ fontFamily: "'Lora', serif" }}>Our Audience</h2>
            <p className="text-stone-500 dark:text-stone-400 max-w-xl mx-auto">
              Readers who are actively invested in their emotional lives and open to products and services that support them.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {audience.map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-4 p-5 rounded-xl bg-stone-50 dark:bg-stone-900 border border-stone-100 dark:border-stone-800"
              >
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-rose-50 dark:bg-rose-900/30 text-rose-500">
                  <i className={`${item.icon} text-base`} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-stone-400 dark:text-stone-500 uppercase tracking-widest mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-stone-700 dark:text-stone-300">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Fit note */}
          <div className="mt-10 p-6 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/40">
            <p className="text-sm text-rose-800 dark:text-rose-300 leading-relaxed">
              <strong>Best-fit advertisers:</strong> Dating apps &amp; platforms, relationship coaching, therapy &amp; mental wellness services, personal development books &amp; courses, lifestyle and self-care brands targeting women 25–44, and any product that genuinely improves how people connect.
            </p>
          </div>
        </section>

        {/* ── Ad Placements ── */}
        <section id="placements" className="bg-stone-50 dark:bg-stone-900 border-y border-stone-100 dark:border-stone-800 py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-3" style={{ fontFamily: "'Lora', serif" }}>Ad Placement Options</h2>
              <p className="text-stone-500 dark:text-stone-400 max-w-xl mx-auto">
                Flexible formats designed to fit your goals — from brand awareness to direct response.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {placements.map((p) => (
                <div
                  key={p.slug}
                  className={`relative flex flex-col rounded-xl border p-6 transition-all ${
                    p.highlight
                      ? 'border-rose-300 dark:border-rose-700 bg-white dark:bg-stone-950'
                      : 'border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950'
                  }`}
                >
                  {p.highlight && (
                    <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 text-xs font-semibold">
                      Popular
                    </span>
                  )}
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 mb-4">
                    <i className={`${p.icon} text-lg`} />
                  </div>
                  <h3 className="text-base font-bold text-stone-800 dark:text-stone-100 mb-2">{p.title}</h3>
                  <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed flex-1 mb-4">{p.description}</p>
                  <div className="space-y-1.5 text-xs text-stone-400 dark:text-stone-500 mb-5">
                    <p><span className="font-semibold text-stone-500 dark:text-stone-400">Size:</span> {p.size}</p>
                    <p><span className="font-semibold text-stone-500 dark:text-stone-400">Impressions:</span> {p.impressions}</p>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-stone-100 dark:border-stone-800">
                    <span className="text-lg font-bold text-rose-500">{p.price}</span>
                    <a
                      href="#contact"
                      className="text-xs font-semibold text-stone-600 dark:text-stone-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Inquire →
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-stone-400 dark:text-stone-500 mt-8">
              All prices in USD. Discounts available for 3-month+ commitments. Custom packages welcome.
            </p>
          </div>
        </section>

        {/* ── Why Partner ── */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-5" style={{ fontFamily: "'Lora', serif" }}>
                Why Advertise With Us?
              </h2>
              <ul className="space-y-4">
                {[
                  { icon: 'ri-focus-3-line', title: 'Intent-driven readers', body: 'People who seek out relationship content are actively working on their lives — and open to tools, products, and services that genuinely help.' },
                  { icon: 'ri-search-eye-line', title: 'High organic traffic', body: '72% of our traffic comes from Google search — meaning readers arrive with purpose, not passively scrolling a feed.' },
                  { icon: 'ri-emotion-happy-line', title: 'Trusted editorial voice', body: 'Readers come to us for honest, research-backed content. That trust extends to brands we choose to work with.' },
                  { icon: 'ri-line-chart-line', title: 'Transparent reporting', body: 'Monthly performance reports with real numbers. No inflated vanity metrics — just honest data on how your campaign performed.' },
                ].map((item) => (
                  <li key={item.title} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-rose-50 dark:bg-rose-900/30 text-rose-500 mt-0.5">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 dark:text-stone-100 text-sm mb-0.5">{item.title}</p>
                      <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">{item.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-square max-w-sm mx-auto lg:max-w-none">
              <img
                src="https://readdy.ai/api/search-image?query=person%20reading%20relationship%20article%20on%20laptop%20at%20cozy%20home%20setting%20warm%20afternoon%20light%20engaged%20reader%20lifestyle%20authentic&width=600&height=600&seq=adv-reader&orientation=squarish"
                alt="Engaged reader on roameurope.blog"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="bg-stone-50 dark:bg-stone-900 border-y border-stone-100 dark:border-stone-800 py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <h2 className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-10 text-center" style={{ fontFamily: "'Lora', serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer group"
                    aria-expanded={openFaq === i}
                  >
                    <span className="font-semibold text-sm text-stone-800 dark:text-stone-100 group-hover:text-rose-500 transition-colors pr-4">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-stone-400 dark:text-stone-500">
                      <i className={`transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-sm text-stone-500 dark:text-stone-400 leading-relaxed border-t border-stone-100 dark:border-stone-800 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact Form ── */}
        <section id="contact" className="max-w-2xl mx-auto px-4 md:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-stone-800 dark:text-stone-100 mb-3" style={{ fontFamily: "'Lora', serif" }}>
              Start a Conversation
            </h2>
            <p className="text-stone-500 dark:text-stone-400">
              Tell us about your brand and what you're looking to achieve. We'll get back to you within 1–2 business days.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-14 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/40">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-500">
                <i className="ri-check-line text-xl" />
              </div>
              <p className="font-semibold text-stone-800 dark:text-stone-100 mb-1">Message received!</p>
              <p className="text-sm text-stone-500 dark:text-stone-400">We'll be in touch within 1–2 business days.</p>
            </div>
          ) : (
            <form
              id="advertise-inquiry-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5" htmlFor="adv-name">
                    Your Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="adv-name"
                    name="name"
                    type="text"
                    required
                    placeholder="Jane Smith"
                    className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-stone-800 dark:text-stone-200 placeholder-stone-400 px-4 py-2.5 text-sm outline-none focus:border-rose-400 dark:focus:border-rose-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5" htmlFor="adv-email">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="adv-email"
                    name="email"
                    type="email"
                    required
                    placeholder="jane@yourbrand.com"
                    className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-stone-800 dark:text-stone-200 placeholder-stone-400 px-4 py-2.5 text-sm outline-none focus:border-rose-400 dark:focus:border-rose-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5" htmlFor="adv-brand">
                  Brand / Company Name <span className="text-rose-500">*</span>
                </label>
                <input
                  id="adv-brand"
                  name="brand"
                  type="text"
                  required
                  placeholder="Your Brand, Inc."
                  className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-stone-800 dark:text-stone-200 placeholder-stone-400 px-4 py-2.5 text-sm outline-none focus:border-rose-400 dark:focus:border-rose-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5" htmlFor="adv-placement">
                  Placement Interest
                </label>
                <select
                  id="adv-placement"
                  name="placement"
                  className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-stone-800 dark:text-stone-200 px-4 py-2.5 text-sm outline-none focus:border-rose-400 dark:focus:border-rose-500 transition-colors cursor-pointer"
                >
                  <option value="">Select a placement (optional)</option>
                  <option value="Hero Banner">Hero Banner — $180/mo</option>
                  <option value="Sidebar Rectangle">Sidebar Rectangle — $120/mo</option>
                  <option value="In-Article Ad">In-Article Ad — $140/mo</option>
                  <option value="Sponsored Article">Sponsored Article — $350/post</option>
                  <option value="Newsletter Mention">Newsletter Mention — $90/send</option>
                  <option value="Brand Bundle">Brand Bundle — $560/mo</option>
                  <option value="Custom / Not sure yet">Custom / Not sure yet</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-1.5" htmlFor="adv-message">
                  Tell Us About Your Campaign <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="adv-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="What are you promoting? Who is your target audience? What's your goal — awareness, clicks, sign-ups? Any other details that will help us understand your needs."
                  className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-stone-800 dark:text-stone-200 placeholder-stone-400 px-4 py-2.5 text-sm outline-none focus:border-rose-400 dark:focus:border-rose-500 transition-colors resize-none"
                />
                <p className="text-xs text-stone-400 mt-1">Max 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                ) : (
                  <><i className="ri-send-plane-line" /> Send Inquiry</>
                )}
              </button>

              <p className="text-xs text-center text-stone-400 dark:text-stone-500">
                By submitting, you agree to our{' '}
                <Link to="/privacy-policy" className="underline hover:text-rose-500 transition-colors">Privacy Policy</Link>.
                We never share your information with third parties.
              </p>
            </form>
          )}
        </section>

      </main>

      <Footer />
    </>
  );
}
